/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef } from 'react'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import CardActions from '@material-ui/core/CardActions'
import Chip from '@material-ui/core/Chip'
import SendIcon from '@material-ui/icons/Send'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import clsx from 'clsx'
import { useFormik } from 'formik'
import type { FormikHelpers } from 'formik'
import { TranslationDictionary } from '@plandi/common'

import { useChatStyles } from './chat.styles'
import { ChatMessage } from './chat-message'
import { ChatMessageSkeleton } from './chat-message-skeleton'
import dynamic from 'next/dynamic'
type FormikValues = {
  message: string
}

type ChatMessageType = {
  id?: number
  text?: string
  createdAt?: string
  name?: string
  avatarUrl?: string | null
  profileId?: number
  isAuthor?: boolean
}

type ChatProps<T extends ChatMessageType> = {
  senderId?: number
  messages?: T[]
  onSendMessage?: (
    values: FormikValues,
    helpers: FormikHelpers<FormikValues>
  ) => void | Promise<void>
  informationMessages?: (any | string | undefined)[]
  disabled?: boolean
  extraActions?: React.ReactNode
  maxLength?: number
  isLoading?: boolean
  translate?: TranslationDictionary['data']
}

export const SChat = <T extends ChatMessageType>(props: ChatProps<T>) => {
  const classes = useChatStyles()
  const chatRef = useRef<HTMLDivElement | null>(null)

  const formik = useFormik({
    initialValues: {
      message: ''
    },
    onSubmit: async (values, helpers) => {
      const value = values.message.trim()

      if (value.length > 0) {
        await props.onSendMessage?.({ message: value }, helpers)

        helpers.setSubmitting(false)
        helpers.resetForm()
      }
    }
  })

  const isDisallowSendMessage =
    formik.isSubmitting ||
    !formik.isValid ||
    !formik.dirty ||
    !formik.values.message.trim().length ||
    props?.disabled

  useEffect(() => {
    chatRef.current?.scroll({
      top: chatRef.current.scrollHeight,
      behavior: 'smooth'
    })
  }, [props?.messages?.length])

  return (
    <Card key={`modelChat-${props.senderId}`}>
      <CardContent className={classes.cardContent} ref={chatRef}>
        {props.isLoading
          ? [...new Array(5)].map((_, i) => (
              <ChatMessageSkeleton
                key={i}
                position={i % 2 === 0 ? 'end' : 'start'}
              />
            ))
          : (props?.messages ?? [])?.map((message) => (
              <ChatMessage
                key={message?.id}
                {...message}
                position={
                  message.isAuthor ||
                  (message.profileId &&
                    props.senderId &&
                    message.profileId === props.senderId)
                    ? 'end'
                    : 'start'
                }
              />
            ))}
        {props.informationMessages?.map((informartionMessage, index) => (
          <div key={index} className={classes.chip}>
            <Chip label={informartionMessage} />
          </div>
        ))}
      </CardContent>
      <CardActions>
        <div className={classes.formContainer}>
          <TextField
            value={formik.values.message}
            onChange={formik.handleChange}
            name="message"
            variant="outlined"
            disabled={formik.isSubmitting || props?.disabled}
            label={props.translate?.message}
            fullWidth
            multiline
            onKeyPress={(event) => {
              if (event.key === 'Enter' && !isDisallowSendMessage) {
                formik.handleSubmit()
              }
            }}
            rowsMax={3}
            className={classes.input}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position="start"
                  onClick={() => {
                    if (!isDisallowSendMessage) {
                      formik.handleSubmit()
                    }
                  }}
                  className={clsx({
                    [classes.sendIcon]: !isDisallowSendMessage
                  })}
                >
                  <SendIcon color="action" />
                </InputAdornment>
              ),
              inputProps: {
                maxLength: props.maxLength ?? 4096
              }
            }}
          />
          {props.extraActions}
        </div>
      </CardActions>
    </Card>
  )
}
export const Chat = dynamic(() => Promise.resolve(SChat), { ssr: false })
